import {
  fetchContactsConversations,
  fetchScheduledConversations,
} from '@/store/contacts/actions';
import {
  selectCandidateById,
  selectContactConversations,
  selectContactConversationsIsLoading,
  selectScheduledConversations,
  selectScheduledConversationsIsLoading,
} from '@/store/contacts/selectors';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Center, Flex, useDisclosure, Accordion } from '@chakra-ui/react';
import { CenterSpinner, EmailTemplates } from '@/components/ats';
import { groupMessagesByThreadOrRef } from '@/components/contacts/candidate-job-drawer/tabs/conversations/utils';
import { useUsersQuery } from '@/store/query/account-invite';
import { selectUsers } from '@/store/users/selectors';
import { useShowToast } from '@/hooks';
import {
  useSendEmailConversationReplyMutation,
  useGetEmailActivityMessageByIdMutation,
} from '@/store/query/email-activity';
import { useState } from 'react';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { ConversationSection } from '@/components/contacts/candidate-job-drawer/tabs/conversations/ConversationSection';
import { useEmailActions } from '@/hooks/useEmailActions';
import { transformUserIdsToEmails } from '@/utils/commonHelpers';

const StyledContainer = styled.div`
  .card {
    .border {
      border-left-width: 1px;
      margin-bottom: 30px;
    }
    &:last-child {
      .border {
        border: 0;
      }
    }
  }
`;

export const CandidateConversations = () => {
  const candidate = useSelector(selectCandidateById);
  const isLoading = useSelector(selectContactConversationsIsLoading);
  const conversations = useSelector(selectContactConversations);
  const scheduledConversations = useSelector(selectScheduledConversations);
  const scheduledConversationsLoading = useSelector(
    selectScheduledConversationsIsLoading
  );

  const dispatch = useDispatch();

  const replyEmailModal = useDisclosure();
  const scheduledEmailModal = useDisclosure();
  const scheduleRef = useRef(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const users = useSelector(selectUsers);
  const toast = useShowToast();
  const { processFromUserPayload, feSharedEmails } = useGetAllUsers();
  const [accordionIndexes, setAccordionIndexes] = useState([1]);
  const [isScheduled, setIsScheduled] = useState(false);
  const [emailActivityData, setEmailActivityData] = useState(null);
  const cc_emails = transformUserIdsToEmails(
    emailActivityData?.cc_user_ids,
    users
  );
  const bcc_emails = transformUserIdsToEmails(
    emailActivityData?.bcc_user_ids,
    users
  );

  const { data: defaultAllowedFromUsers = [] } = useUsersQuery({
    params: {
      scope: 'email_delegatable',
    },
  });

  const [getEmailActivityMessageById] =
    useGetEmailActivityMessageByIdMutation();

  const {
    handleSendNow,
    handleDeleteEmail,
    handleEditEmailSubmit,
    isUpdating,
  } = useEmailActions({
    onSuccess: () => {
      dispatch(fetchScheduledConversations({ contactId: candidate?.id }));
    },
    contactId: candidate?.id,
  });

  useEffect(() => {
    dispatch(fetchContactsConversations({ contactId: candidate?.id }));
    dispatch(fetchScheduledConversations({ contactId: candidate?.id }));
  }, [candidate]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedMessagesObj = groupMessagesByThreadOrRef(
    conversations?.messages
  );
  const groupedMessages = Object.values(groupedMessagesObj);

  const groupedScheduledMessagesObj = groupMessagesByThreadOrRef(
    scheduledConversations?.messages
  );
  const groupedScheduledMessages = Object.values(groupedScheduledMessagesObj);

  const [sendEmailConversationReply, { isLoading: isSending }] =
    useSendEmailConversationReplyMutation();

  if (isLoading || scheduledConversationsLoading) return <CenterSpinner />;

  if (groupedMessages?.length === 0 && groupedScheduledMessages?.length === 0)
    return (
      <Center h="100%">No conversations found for the candidate on Kula</Center>
    );

  const handleSendEmail = async (values) => {
    try {
      let fromPayload = {};

      // todo: clean up once shared emails is tested
      if (feSharedEmails) {
        fromPayload = processFromUserPayload({
          selectedUser: values?.fromUserObject,
          // payloadKey: 'from',
          payloadKey: 'sender_id',
        });
      } else {
        const { from } = values;
        const sender_id = users?.find((u) => u?.email === from)?.id;
        fromPayload = { sender_id };
      }
      const params = {
        body: values.body,
        attachments: values.attachments,
        cc_emails: values.cc,
        bcc_emails: values.bcc,
        commTempId: values.selectedTemplate?.id,
        ...fromPayload,
        // sender_id: users?.find((u) => u?.email === values.from)?.id,
      };
      let replySubject;
      if (selectedMessage?.subject) {
        replySubject = selectedMessage.subject.startsWith('Re:')
          ? selectedMessage.subject
          : `Re: ${selectedMessage.subject}`;
      }
      params.subject = replySubject || values.subject;
      const result = await sendEmailConversationReply({
        candidate_message_id: selectedMessage?.id,
        params,
      });
      if (result?.error?.length) {
        toast({
          title: 'Sending email failed',
          status: 'error',
        });
      } else {
        toast({ title: 'Reply email sent successfully' });
        replyEmailModal.onClose();
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onEditEmailSubmit = (values, onClose) => {
    handleEditEmailSubmit(
      {
        ...values,
        messageId: selectedMessage.id,
        isScheduled: isScheduled,
      },
      onClose,
      scheduleRef
    );
  };

  const handleEditEmail = async (message) => {
    if (!accordionIndexes.includes(0)) {
      setAccordionIndexes([...accordionIndexes, 0]);
    }
    setSelectedMessage(message);
    const response = await getEmailActivityMessageById({
      messageId: message?.id,
    });
    if ('data' in response) {
      setEmailActivityData(response?.data);
      // Set isScheduled based on the email activity data
      setIsScheduled(response?.data?.stage === 'scheduled');
      scheduledEmailModal.onOpen();
    }
  };

  const transfer_info = conversations?.transfer_info;

  return (
    <>
      <Flex flexDir="column" maxH="100%" overflowY="auto" p="0">
        <StyledContainer>
          <Accordion
            allowToggle
            defaultIndex={[1]}
            index={accordionIndexes}
            onChange={(indexes) =>
              setAccordionIndexes(Array.isArray(indexes) ? indexes : [indexes])
            }
            reduceMotion
            allowMultiple
          >
            <ConversationSection
              title="Scheduled"
              data={groupedScheduledMessages}
              transfer_info={transfer_info}
              handleReplyClick={(message) => {
                if (!accordionIndexes.includes(0)) {
                  setAccordionIndexes([...accordionIndexes, 0]);
                }
                setSelectedMessage(message);
                replyEmailModal.onOpen();
              }}
              handleCandidateReply={(message) => {
                if (!accordionIndexes.includes(0)) {
                  setAccordionIndexes([...accordionIndexes, 0]);
                }
                setSelectedMessage(message);
                replyEmailModal.onOpen();
              }}
              handleSendNow={handleSendNow}
              handleDeleteEmail={handleDeleteEmail}
              handleEditEmail={handleEditEmail}
              onRefetchScheduled={() =>
                dispatch(
                  fetchScheduledConversations({ contactId: candidate?.id })
                )
              }
            />
            <ConversationSection
              title="Conversation History"
              data={groupedMessages}
              transfer_info={transfer_info}
              handleReplyClick={(message) => {
                if (!accordionIndexes.includes(1)) {
                  setAccordionIndexes([...accordionIndexes, 1]);
                }
                setSelectedMessage(message);
                replyEmailModal.onOpen();
              }}
              handleCandidateReply={(message) => {
                if (!accordionIndexes.includes(1)) {
                  setAccordionIndexes([...accordionIndexes, 1]);
                }
                setSelectedMessage(message);
                replyEmailModal.onOpen();
              }}
              handleSendNow={() => {}}
              handleDeleteEmail={() => {}}
              handleEditEmail={() => {}}
            />
          </Accordion>
        </StyledContainer>
      </Flex>
      {replyEmailModal.isOpen && selectedMessage && (
        <EmailTemplates
          initialValues={{
            ...selectedMessage,
            sender:
              selectedMessage?.sender?.sent_by === 'candidate'
                ? undefined
                : {
                    ...selectedMessage?.sender,
                    isSharedEmail:
                      selectedMessage?.sender?.sent_by_type ===
                      'AtsSharedEmail',
                    ...selectedMessage?.sent_from,
                  },
          }}
          formNameKey="sendTemplateId"
          channel="email"
          isReplyMode={true}
          mode="create"
          renderOnMount
          emailFormSubmit={handleSendEmail}
          modalConfig={{
            modalTitle: 'Send email',
            modalDescription:
              'Compose your email from scratch or use an email template',
          }}
          modalActions={replyEmailModal}
          defaultAllowedFromUsers={defaultAllowedFromUsers}
          isModalCtaLoading={isSending || isUpdating}
          skipTemplateSelection={true}
        />
      )}
      {scheduledEmailModal.isOpen && selectedMessage && (
        <EmailTemplates
          formNameKey="sendTemplateId"
          mode="create"
          isEditMode={true}
          initialValues={{
            // from: emailActivityData?.ats_shared_email
            //   ? emailActivityData?.ats_shared_email?.email
            //   : users?.find((user) => user?.id === emailActivityData?.sender_id)
            //       ?.email,
            cc: cc_emails,
            bcc: bcc_emails,
            subject: emailActivityData?.ats_comm_template?.subject,
            body: emailActivityData?.ats_comm_template?.body,
            attachments: emailActivityData?.ats_comm_template?.attachments,
            sender:
              selectedMessage?.sender?.sent_by === 'candidate'
                ? undefined
                : {
                    ...selectedMessage?.sender,
                    isSharedEmail:
                      selectedMessage?.sender?.sent_by_type ===
                      'AtsSharedEmail',
                    ...selectedMessage?.sent_from,
                  },
          }}
          selected={emailActivityData}
          renderOnMount
          emailFormSubmit={onEditEmailSubmit}
          modalConfig={{
            modalTitle: 'Edit email',
            modalDescription: 'Edit your email content or scheduling',
          }}
          modalActions={scheduledEmailModal}
          modalOnClose={() => {
            setSelectedMessage(null);
          }}
          defaultAllowedFromUsers={defaultAllowedFromUsers}
          isModalCtaLoading={isUpdating}
          scheduleRef={scheduleRef}
          isScheduleEmail={true}
          isScheduled={isScheduled}
          setIsScheduled={setIsScheduled}
        />
      )}
    </>
  );
};
